import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  ChevronUp,
  Edit,
  Copy,
  Trash,
  Bed,
  ClockArrowDown
  , GripVertical
} from "lucide-react";
import { useState, useRef } from "react";
import { ItineraryActivity } from "../models/activity.model";
import { getActivityTypeIcon } from "../models/activity-configs.model";

interface BaseActivityCardProps {
  activity: ItineraryActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  // Drag & drop handlers (optional)
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId: string | undefined) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
  children: {
    alwaysVisible: React.ReactNode;
    collapsible: React.ReactNode;
  };
}

export const BaseActivityCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  children,
  isTraveler = false,
  isAdmin = false
}: BaseActivityCardProps) => {
  const [isExpanded, setIsExpanded] = useState(isTraveler);
  const isOutActivity = activity.inOrOutActivity === 'out';
  const isInActivity = activity.inOrOutActivity === 'in';

  // ref used to store temporary drag image element so we can clean it up on drag end
  const dragImageRef = useRef<HTMLElement | null>(null);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div
        data-activity-card={`activity-${activity.id}`}
        className="w-full mb-2 border flex flex-row items-center border-gray-200 rounded-2xl p-4 pl-0 bg-white shadow-sm relative transition-colors duration-200 hover:border-gray-300"
      >

        {!isTraveler && (
          <div className="pr-2 ml-1">
            {/* Drag handle - visible when parent provides drag handlers */}
            {onDragStart && onDropBefore && (
              <button
                aria-label="Drag to reorder"
                draggable={!isExpanded}
                onDragStart={(e) => {
                  if (isExpanded) return;
                  e.dataTransfer.setData('text/plain', activity.id);
                  e.dataTransfer.effectAllowed = 'move';

                  // Try to show the whole card as drag image. We clone the card element,
                  // position it offscreen, and pass it to setDragImage with proper offsets.
                  try {
                    const buttonEl = e.currentTarget as HTMLElement;
                    const cardEl = buttonEl.closest('[data-activity-card]') as HTMLElement | null;
                    if (cardEl && typeof e.dataTransfer.setDragImage === 'function') {
                      const rect = cardEl.getBoundingClientRect();
                      const clone = cardEl.cloneNode(true) as HTMLElement;
                      // style clone so it renders similarly and doesn't interfere with layout
                      clone.style.boxSizing = 'border-box';
                      clone.style.width = `${rect.width}px`;
                      clone.style.position = 'absolute';
                      clone.style.top = '-9999px';
                      clone.style.left = '-9999px';
                      clone.style.margin = '0';
                      clone.style.pointerEvents = 'none';
                      clone.style.opacity = '0.98';
                      // optional nice shadow so it appears lifted
                      clone.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)';

                      document.body.appendChild(clone);
                      dragImageRef.current = clone;

                      const offsetX = Math.max(0, (e.clientX - rect.left));
                      const offsetY = Math.max(0, (e.clientY - rect.top));

                      e.dataTransfer.setDragImage(clone, offsetX, offsetY);
                    }
                  } catch (err) {
                    // fallback: if setDragImage with element fails, silently ignore
                  }

                  if (onDragStart) onDragStart(activity);
                }}
                onDragEnd={() => {
                  // clean up temporary drag image if exists
                  if (dragImageRef.current) {
                    try { document.body.removeChild(dragImageRef.current); } catch (e) { }
                    dragImageRef.current = null;
                  }

                  if (onDragEnd) onDragEnd();
                }}
                aria-disabled={isExpanded}
                className={`p-2 rounded w-8 h-8 flex items-center justify-center ${isExpanded ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100 cursor-grab'}`}
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </button>
            )}

          </div>
        )}
        <div className={`overflow-hidden flex-1 ${isTraveler ? 'pl-6' : 'pl-2'}`}>
          {/* Header with icon, title and menu */}
          <div className="flex items-center gap-3 flex-1">
            {/* Activity Type Icon */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gray-200`}>
              {activity.type === 'accommodation' && isOutActivity
                ? <ClockArrowDown className="h-4 w-4" />
                : activity.type === 'accommodation' && isInActivity
                  ? <Bed className="h-4 w-4" />
                  : getActivityTypeIcon(activity.type)
              }
            </div>

            {/* Title */}
            <h4 className="text-lg font-semibold text-gray-900 truncate flex-1">
              {activity.title}
              {activity.type === 'accommodation' && isInActivity && ' (Check-in)'}
              {activity.type === 'accommodation' && isOutActivity && ' (Check-out)'}
            </h4>

            {/* Right side - Menu button - Only show if not read-only */}
            {!isReadOnly && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-12 h-12 rounded-full hover:bg-gray-100"
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem disabled={isOutActivity} onClick={() => onEdit(activity)} className="flex items-center gap-2">
                    <Edit className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  {onDuplicate && (
                    <DropdownMenuItem disabled={isOutActivity} onClick={() => onDuplicate(activity)} className="flex items-center gap-2">
                      <Copy className="h-4 w-4" />
                      Duplicate
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => onDelete(activity)}
                    disabled={isOutActivity}
                    className="flex items-center gap-2 text-red-600 focus:text-red-600"
                  >
                    <Trash className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Activity Details - Always visible */}
          <div className="flex items-center gap-8 my-4 text-sm text-gray-500">
            {children.alwaysVisible}
          </div>

          {/* Collapsible Content with animation */}
          <CollapsibleContent className="space-y-4 overflow-hidden transition-all duration-1000 ease-in data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-12 data-[state=open]:slide-in-from-top-12">
            <div className="pt-2">
              {children.collapsible}
            </div>
          </CollapsibleContent>

          {/* Expand/Collapse Button */}
          <div className="flex items-center justify-center mt-4">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Less
                  </>
                ) : (
                  <>
                    <ChevronUp className="h-4 w-4 rotate-180" />
                    More
                  </>
                )}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>
      </div>
    </Collapsible>
  );
};
